import { FusionInputField } from 'components';
import BaseModal from 'components/Modals/BaseModal';
import ConfirmModal from 'components/UI/ConfirmPopup';
import RoadDefinerMap from 'features/integrations/components/RoadDefinerMap';
import { USGS_PROVISIONAL_STATEMENT } from 'features/integrations/features/ConnectedDevices/assets/USGSProvisionalStatment';
import { useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { IoMdInformationCircleOutline } from 'react-icons/io';
import { Marker } from 'react-leaflet';
import { useDispatch, useSelector } from 'react-redux';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import { getDynamicDeviceIcon, getDynamicDeviceIconFromType } from 'utils/iconUtils';
import {
  selectUsgsProvisonalAccepted,
  setUsgsProvisonalAccepted,
} from '../../../../../store/slices/Widget/widgetSlice';
import { calculateCenterAndZoom, getDeviceLocationsByIds } from '../utils/groupUtils';
import DeviceMultiSelector from './DeviceMultiSelector';

/**
 * @param {{submit: (formData: {name: string, comments: string, devices: number[], type: string, coordinates: number[][]}), cancel: (), initialData?: Object, isSubmitting?: boolean}}
 * @returns
 */
export default function NewGroup({
  submit,
  cancel,
  initialData = {},
  show,
  setShow,
  isSubmitting = false,
}) {
  const getInitialFormData = (source = {}) => ({
    name: '',
    comments: '',
    devices: [],
    type: '',
    coordinates: [],
    ...source,
  });

  const { data: connectedDevices = [], isLoading: connectedDevicesLoading } =
    useGetConnectedDevicesQuery();
  const dispatch = useDispatch();
  const provisionalAccepted = useSelector(selectUsgsProvisonalAccepted);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);

  const [formData, setFormData] = useState(getInitialFormData(initialData));

  const [mapCenter, setMapCenter] = useState();
  const [mapZoom, setMapZoom] = useState();
  const isEditMode = Object.keys(initialData).length > 0;
  const isFormLoading = connectedDevicesLoading;

  const hasUSGSDevices = useMemo(
    () =>
      formData.devices.some(
        (id) => connectedDevices.find((d) => d.id === id)?.system_type === 'USGS',
      ),
    [formData.devices, connectedDevices],
  );

  useEffect(() => {
    setFormData(getInitialFormData(initialData));
  }, [initialData]);

  // Keep form state in sync with selector and recalc map framing
  const onDevicesChanged = (value, deviceType) => {
    setFormData({ ...formData, devices: value, type: deviceType });

    const valueDevices = getDeviceLocationsByIds(value, connectedDevices);

    const { center, zoom } = calculateCenterAndZoom(valueDevices);
    setMapCenter(center);
    setMapZoom(zoom);
  };

  const getDeviceMarker = (deviceId) => {
    const device = connectedDevices?.find((device) => device.id === deviceId);

    if (!device) return;

    // Special case for USGS Monitoring Stations to use their specific icons
    // Needed because the icons rely on data inside the device, not just the device_type
    if (device.system_type === 'USGS' && device.device_type === 'Monitoring Station') {
      return (
        <Marker
          key={deviceId}
          draggable={false}
          position={device.location || { lat: 0, lng: 0 }}
          icon={getDynamicDeviceIcon(device, 'icon', [30, 30])}
        >
          {device.alias}
        </Marker>
      );
    }

    return (
      <Marker
        key={deviceId}
        draggable={false}
        position={device.location || { lat: 0, lng: 0 }}
        icon={getDynamicDeviceIconFromType(device?.system_type, device?.device_type)}
      >
        {device.alias}
      </Marker>
    );
  };

  return (
    <BaseModal
      show={show}
      handleShow={setShow}
      title={isEditMode ? 'Edit Group' : 'New Group'}
      size="xl"
    >
      <div className="flex-lg-row flex-column w-100 d-flex justify-content-around">
        {/* Input Fields */}
        <div className="d-flex flex-row gap-3">
          <div className="d-flex flex-column overflow-hidden">
            <div>Group Name</div>
            <FusionInputField
              value={formData?.name}
              onChange={(value) => setFormData({ ...formData, name: value })}
            />

            <div className="d-flex flex-column overflow-hidden flex-shrink">
              <div>Comments</div>
              <div className="d-flex bg-transparent rounded-3 border border-1 border-white overflowy-hidden">
                <textarea
                  className="d-flex overflowy-hidden"
                  rows={1}
                  value={formData.comments}
                  onInput={(e) => {
                    e.target.style.height = 'auto';
                    e.target.style.height = `${e.target.scrollHeight}px`;

                    setFormData({ ...formData, comments: e.target.value });
                  }}
                />
              </div>
            </div>

            {/* Device Selector */}
            <div className="mt-3 customscrollbar-y p-2 d-flex flex-column overflow-hidden flex-shrink bg-primary rounded-3 flex-grow-full min-h-sm">
              <div className="p-0">Device List</div>
              <DeviceMultiSelector
                value={formData.devices}
                deviceType={formData.type}
                onChange={onDevicesChanged}
              />
            </div>
          </div>
        </div>
        {/* Map input */}
        <div className="d-flex flex-column gap-3 mt-3 flex-grow-full max-w-lg-50">
          <RoadDefinerMap
            value={formData.coordinates || [mapCenter]}
            onChange={(value) => setFormData({ ...formData, coordinates: value })}
            initialCenter={mapCenter}
            initialZoom={mapZoom}
          >
            {formData.devices.map(getDeviceMarker)}
          </RoadDefinerMap>

          {/* USGS provisional attribution — always visible so users can review before adding USGS devices */}
          <div
            className="d-flex align-items-center justify-content-start gap-2 px-1"
            style={{ fontSize: 10 }}
          >
            <IoMdInformationCircleOutline
              className="rounded-circle bg-primary p-1 cursor-pointer flex-shrink-0"
              style={{ height: 26, width: 26 }}
              onClick={() => setShowDisclaimerModal(true)}
            />
            <p className="mb-0 text-white opacity-75">
              USGS Data may be{' '}
              <span
                className="text-decoration-underline cursor-pointer"
                onClick={() => setShowDisclaimerModal(true)}
              >
                provisional
              </span>
            </p>
          </div>

          {/* Submit button */}
          <div className="d-flex flex-row justify-content-end">
            <Button onClick={() => submit(formData)} disabled={isFormLoading || isSubmitting}>
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Saving...</span>
                  </span>
                  {isEditMode ? 'Saving...' : 'Creating...'}
                </>
              ) : isFormLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </span>
                  Loading devices...
                </>
              ) : isEditMode ? (
                'Save'
              ) : (
                'Create'
              )}
            </Button>
          </div>
        </div>
      </div>
      <ConfirmModal
        open={!provisionalAccepted || showDisclaimerModal}
        setOpen={setShowDisclaimerModal}
        options={['Continue']}
        callbacks={[
          () => {
            dispatch(setUsgsProvisonalAccepted(true));
            setShowDisclaimerModal(false);
          },
        ]}
        message={USGS_PROVISIONAL_STATEMENT}
        closeAction={() => {}}
        size="lg"
      />
    </BaseModal>
  );
}
